import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App.jsx";
import { DemoModeProvider } from "./lib/DemoModeContext.jsx";
import { apiFetch } from "./lib/api.js";
import { sledujKlavesnici } from "./lib/klavesnice.js";
import "./index.css";

// Skutečnou výšku okna nad klávesnicí zná na iOS jenom visualViewport. Bez
// tohohle skončí okno i spodní lišta pod klávesnicí i s polem, do kterého se
// píše. Zapisuje --klavesnice a --vv-vyska, viz src/lib/klavesnice.js.
sledujKlavesnici();

// Defenzivně odregistrovat případný service worker — v minulosti mohly zaregistrovat cache
// která přepisuje nové JS/CSS buildy a uživatel vidí stará data. Tohle nic neregistruje,
// jen zruší existující. Spouští se před renderem, běží asynchronně na pozadí.
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => { /* silent — SW API může selhat v privátním režimu */ });
  // Vynutit aktualizaci všech cachí
  if (window.caches && window.caches.keys) {
    window.caches.keys()
      .then((keys) => keys.forEach((k) => window.caches.delete(k)))
      .catch(() => {});
  }
}

// Globální zachycení JS chyb — pošle do /api/system-errors aby owner viděl crash UI v banneru.
// Throttle: stejná chyba (message+filename) max 1× za 5 minut, aby se nezacpal sheet při loop crashi.
(function setupErrorReporter() {
  if (typeof window === "undefined") return;
  const recentErrors = new Map(); // "msg|file" → timestamp
  const THROTTLE_MS = 5 * 60 * 1000;
  // Šum, který se NIKDY neloguje (nedává info a jen zaplevelí notifikace):
  //  - "Script error." = cross-origin chyba bez detailu (typicky z rozšíření prohlížeče, Grammarly…)
  //  - chyby pocházející z přípon prohlížeče (chrome-extension:// apod.)
  //  - benigní ResizeObserver loop
  const isNoise = (info) => {
    const msg = String(info.message || "").trim();
    const file = String(info.filename || "");
    if (!msg) return true;
    if (/^Script error\.?$/i.test(msg)) return true;
    if (/ResizeObserver loop/i.test(msg)) return true;
    if (/^(chrome|moz|safari)-extension:\/\//.test(file)) return true;
    if (/extension:\/\//.test(String(info.stack || ""))) return true;
    return false;
  };
  const reportError = (info) => {
    try {
      if (isNoise(info)) return; // nehlásit šum z prohlížeče/rozšíření
      const key = `${info.message || ""}|${info.filename || ""}`;
      const last = recentErrors.get(key) || 0;
      if (Date.now() - last < THROTTLE_MS) return; // throttled
      recentErrors.set(key, Date.now());
      apiFetch("/api/system-errors", {
        method: "POST",
        body: JSON.stringify({
          action: "log",
          source: "frontend",
          severity: "error",
          code: "JS_EXCEPTION",
          message: String(info.message || "").substring(0, 300),
          detail: `${info.filename || ""}:${info.lineno || ""}:${info.colno || ""}\n${info.stack || ""}`.substring(0, 800),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* silent */ }
  };
  window.addEventListener("error", (e) => {
    reportError({ message: e.message, filename: e.filename, lineno: e.lineno, colno: e.colno, stack: e.error?.stack });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason || {};
    reportError({ message: reason.message || String(reason).substring(0, 200), stack: reason.stack });
  });
})();

// 17. 10. 2026: po nasazení nové verze stará otevřená stránka nenajde své dílčí soubory
// (Failed to fetch dynamically imported module, chodilo jako chyba do Slacku). Vite na to
// pošle událost vite:preloadError, stránku jednou obnovíme a načte se nová verze.
// Pojistka v sessionStorage, ať se při skutečném výpadku sítě neobnovuje dokola.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (e) => {
    try {
      const klic = "fw40_preload_reload";
      const posledni = Number(sessionStorage.getItem(klic) || 0);
      if (Date.now() - posledni < 60 * 1000) return;
      sessionStorage.setItem(klic, String(Date.now()));
      e.preventDefault();
      window.location.reload();
    } catch { /* bez sessionStorage chybu necháme projít jako dřív */ }
  });
}

// 22.5.2026 trvalé řešení stale bundle: každých 5 min check /api/current-bundle.
// Pokud se hash liší od mého (=user má cached starou SPA, mezitim byl deploy),
// zobrazí dismissible banner "Nová verze, klikni Obnovit". Po reload user dostane
// nový bundle, žádné staré JS_EXCEPTION nemůžou tikat. Backend mezitim drop logy
// z mého starého bundle hash.
(function setupVersionCheck() {
  if (typeof window === "undefined") return;
  // Podpis smlouvy (tablet a telefon klientky, 3. 11. 2026): klientce se lišta o nové verzi nezobrazuje.
  if (/^\/(podpis|p)\//.test(window.location.pathname)) return;
  // Extract můj bundle hash z document scripts (vite injectne <script src="/assets/index-XXXX.js">)
  const scripts = Array.from(document.scripts).map(s => s.src).filter(s => s.includes('/assets/'));
  const myBundle = scripts[0]?.match(/index-([A-Za-z0-9_-]{8})\.js/)?.[1];
  if (!myBundle) return; // dev mode nebo nepoznám bundle

  let bannerShown = false;
  const checkVersion = async () => {
    try {
      const r = await fetch('/api/current-bundle', { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const j = await r.json();
      const currentBundle = j.bundle;
      if (currentBundle && currentBundle !== myBundle && !bannerShown) {
        bannerShown = true;
        showNewVersionBanner();
      }
    } catch { /* silent — banner se ukáže příště */ }
  };

  function showNewVersionBanner() {
    const banner = document.createElement('div');
    banner.id = 'fw40-new-version-banner';
    banner.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:99999;background:linear-gradient(90deg,#241430,#7C5DB0);color:white;padding:12px 16px;display:flex;align-items:center;justify-content:center;gap:12px;font-family:system-ui,-apple-system,sans-serif;font-size:14px;box-shadow:0 2px 8px rgba(0,0,0,0.2)';
    banner.innerHTML = `
      <span>Nová verze aplikace je k dispozici. Obnov stránku ať máš nejnovější funkce a opravy.</span>
      <button id="fw40-reload-btn" style="background:white;color:#5F4690;border:none;padding:6px 14px;border-radius:6px;font-weight:600;cursor:pointer">Obnovit</button>
      <button id="fw40-dismiss-btn" style="background:transparent;color:white;border:1px solid rgba(255,255,255,0.5);padding:6px 12px;border-radius:6px;cursor:pointer;font-size:12px">Později</button>
    `;
    document.body.appendChild(banner);
    document.getElementById('fw40-reload-btn').onclick = () => window.location.reload();
    document.getElementById('fw40-dismiss-btn').onclick = () => banner.remove();
  }

  // První check po 30s (ne hned po loadu — zbytečné), pak každých 5 min.
  setTimeout(checkVersion, 30 * 1000);
  setInterval(checkVersion, 5 * 60 * 1000);
})();

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <BrowserRouter>
      <DemoModeProvider>
        <App />
      </DemoModeProvider>
    </BrowserRouter>
  </React.StrictMode>
);
